import { invalidate } from '@react-three/fiber';
import { type Camera, type OrthographicCamera, PerspectiveCamera, Vector3, BoxHelper } from 'three';

import { EPSILON } from 'shared/constants';

import type { CameraControls } from '@react-three/drei';
import type { PlacementSolution } from 'api/models';

const syncTarget = new Vector3();
const MIN_DISTANCE = 0.1;

export const cameraUpVectors: Record<string, Vector3> = {
    Right: new Vector3(0, 0, 1),
    Left: new Vector3(0, 0, 1),
    Top: new Vector3(0, 1, 0),
    Bottom: new Vector3(0, 1, 0),
    Front: new Vector3(0, 1, 0),
    Back: new Vector3(0, -1, 0)
};

export function getOrthographicVisibleHeight(camera: OrthographicCamera): number {
    return (camera.top - camera.bottom) / Math.max(camera.zoom, EPSILON);
}

export function getPerspectiveVisibleHeightAtDistance(camera: PerspectiveCamera, distance: number): number {
    const effectiveFov = camera.getEffectiveFOV();
    return 2 * Math.tan((effectiveFov * Math.PI) / 360) * Math.max(distance, MIN_DISTANCE);
}

export function getCameraDistance(controls: CameraControls, camera: Camera): number {
    if (camera instanceof PerspectiveCamera && Number.isFinite(controls.distance) && controls.distance > 0) {
        return controls.distance;
    }
    controls.getTarget(syncTarget);
    return camera.position.distanceTo(syncTarget);
}

export function applyPerspectiveFrustum(camera: PerspectiveCamera, aspect: number, distance: number) {
    const safeDistance = Math.max(distance, MIN_DISTANCE);
    camera.aspect = aspect;
    camera.near = Math.max(0.1, safeDistance / 1000);
    camera.far = Math.max(camera.near + 100, safeDistance * 100);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
}

export function applyOrthographicFrustum(
    camera: OrthographicCamera,
    aspect: number,
    visibleHeight: number,
    distance: number
) {
    const safeDistance = Math.max(distance, MIN_DISTANCE);
    const safeZoom = Math.max(camera.zoom, EPSILON);
    const halfHeight = Math.max(visibleHeight * safeZoom * 0.5, 1e-4);
    const halfWidth = halfHeight * aspect;

    camera.left = -halfWidth;
    camera.right = halfWidth;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;
    camera.near = 0.01;
    camera.far = Math.max(camera.near + 100, safeDistance * 100);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
}

export function syncCameraPositions(source: Camera, target: Camera) {
    target.position.copy(source.position);
    target.quaternion.copy(source.quaternion);
    target.up.copy(source.up);
    target.updateMatrixWorld(true);
}

export function getViewDirection(solution: PlacementSolution) {
    const direction = new Vector3();
    solution.placements.forEach((group) =>
        group.instances.forEach((instance) =>
            instance.parts.forEach((part) => direction.add(new Vector3(part.alignment.x, part.alignment.y, 0)))
        )
    );

    if (direction.lengthSq() === 0) {
        direction.set(1, 0, 0);
    }

    return direction.normalize();
}

export function setFocusOnSolution(
    cameraControls: CameraControls | null,
    helper: BoxHelper | undefined,
    solution: PlacementSolution | null
) {
    const boundingSphere = helper?.geometry.boundingSphere;
    if (!cameraControls || !boundingSphere || !solution) {
        return;
    }

    const distance = boundingSphere.radius * 10;
    const direction = getViewDirection(solution);
    const cameraPosition = boundingSphere.center.clone().addScaledVector(direction, distance);
    const fitSphere = boundingSphere.clone();
    fitSphere.radius *= 1.1;

    cameraControls.setLookAt(
        cameraPosition.x,
        cameraPosition.y,
        cameraPosition.z,
        boundingSphere.center.x,
        boundingSphere.center.y,
        boundingSphere.center.z,
        false
    );
    cameraControls.fitToSphere(fitSphere, false);
    cameraControls.camera.updateProjectionMatrix();

    invalidate();
}
